import React from "react";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  FunnelIcon,
  LinkIcon,
  CloudArrowUpIcon,
  IdentificationIcon,
  DocumentTextIcon, 
  EyeIcon,
  UserIcon, 
  PencilIcon,
  CalendarDaysIcon,

} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import { downloadControlFileApi } from "../services/axios/AuthApis/TrustCenterApis";
import { toast } from "../utils/notify";
import ApiLoader from "./ui/ApiLoader";
import { DataTable } from "./common/DataTable";

const ControlsTab = ({
  selectedFramework,
  setControlFormData,
  controls,
  controlsLoading,
  controlsPagination,
  controlsSearchTerm,
  searchInputRef,
  getStatusColor,
  loadControls,
  getFrameworkId,
  setEmployeeDropdown,
  handleControlsSearch,
  setControlsSearchTerm,
  handleControlsPageChange,
  setShowControlModal,
  setShowEvidenceModal,
  setShowControlDetailModal,
  setSelectedControl,
  selectedControlForAssign,
  setSelectedControlForAssign,
  setShowAssignModal,
  setAssignForm,
  fetchCompanyEmployees,
  companyEmployees,
  assignForm,
  employeeSearch,
  setEmployeeSearch,
  employeeDropdown,
  controlsFilters,
  setControlsFilters,
  assignedControlsLoading,


}) => {
  const { user } = useAuth();
  const isSuperAdmin = user.role === "admin" || user.role === "platform_admin";
  const isCustomerAdmin = user?.role === "customer_admin";
  const isEndUser = user?.role === "end_user";
  const allowAssign = isCustomerAdmin || user?.role === "security_analyst";
  const isAuditor = user?.role === "auditor";
  if (!selectedFramework) return null;

const hasActiveControlFilters =
  !!controlsFilters.control_id ||
  !!controlsFilters.title ||
  !!controlsFilters.assigned_to ||
  !!controlsFilters.date;

  const adminColumns = [
    {
      id: "control_id",
      header: "Control ID",
      cell: ({ row }) => <div className="font-medium text-gray-900">{row.original.control_id}</div>,
    },
    {
      id: "control",
      header: "Control",
      cell: ({ row }) => <div className="text-sm text-gray-600">{row.original.title}</div>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const control = row.original;
        return !isAuditor ? (
          <div className="text-center">
            <button
              onClick={() => {
                setControlFormData({ control_id: control.control_id });
                setShowControlModal(true);
                setShowControlDetailModal(true);
              }}
              className="text-blue-600 hover:text-blue-900"
            >
              <PencilIcon className="w-3 h-3" />
            </button>
          </div>
        ) : null;
      },
    },
  ];

  const nonAdminColumns = [
    {
      id: "control_id",
      header: "Control ID",
      cell: ({ row }) => <span className="text-xs font-medium text-blue-700">{row.original.control_id}</span>,
    },
    {
      id: "control",
      header: "Control",
      cell: ({ row }) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.original.title}</div>
          {row.original.description && (
            <div className="text-xs text-gray-500 mt-1">{row.original.description}</div>
          )}
        </div>
      ),
    },
    {
      id: "category",
      header: "Category",
      cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.category || "-"}</span>,
    },
    {
      id: "owner",
      header: "Owner",
      cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.control_owner || "-"}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <span className={`inline-flex px-2 py-1 text-[11px] font-semibold rounded-full border ${getStatusColor(row.original.status || "Assigned")}`}>
          {row.original.status || "Assigned"}
        </span>
      ),
    },
    {
      id: "due_date",
      header: "Due Date",
      cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.due_date || "Not assigned"}</span>,
    },
    {
      id: "assigned",
      header: "Assigned",
      cell: ({ row }) => (
        <span className="text-xs font-medium text-gray-800">
          {row.original.assigned_to ? row.original.assigned_to : isEndUser ? "You" : "Unassigned"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const control = row.original;
        return (
          <div className="space-x-2">
            <button
              onClick={() => handleViewUploadedFile(control.response_id)}
              className="text-blue-600 hover:text-blue-900"
            >
              <EyeIcon className="w-4 h-4" />
            </button>

            {allowAssign && !isEndUser && !control.assigned_to && (
              <button
                onClick={() => {
                  setSelectedControlForAssign({ ...control, mongoId: control.id });
                  if (typeof setEmployeeDropdown === "function") setEmployeeDropdown([]);
                  if (typeof setEmployeeSearch === "function") setEmployeeSearch("");
                  if (typeof setAssignForm === "function") {
                    setAssignForm((prev) => ({ ...prev, employee_name: "", employee_id: "" }));
                  }
                  setShowAssignModal(true);
                }}
                className="text-blue-500 hover:underline"
              >
                Assign
              </button>
            )}

            {!isAuditor && !isEndUser && (
              <button
                onClick={() => {
                  const updated = controls.find((c) => c.id === control.id);
                  setSelectedControl({ ...updated, response_id: updated?.response_id || null });
                  setShowControlDetailModal(true);
                }}
                className="text-blue-600 hover:text-blue-900"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const handleViewUploadedFile = async (responseId) => {
    try {
      if (!responseId) {
        toast.error("No uploaded file available");
        return;
      }

      const data = await downloadControlFileApi(responseId);

      if (data?.download_url) {
        window.open(data.download_url, "_blank");
      } else {
        toast.error("Download URL not found");
      }
    } catch (error) {
      console.error("File view failed", error);
      toast.error("Unable to open file");
    }
  };

  return (
    <div className="space-y-6">
      {/* Framework Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {selectedFramework.name}
            </h2>
            <p className="text-gray-600 mt-1">
              {selectedFramework.description}
            </p>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
              <span>📍 {selectedFramework.region || "Global"}</span>
              <span>🏢 {selectedFramework.type}</span>
              <span>v{selectedFramework.version || "1.0"}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">
              {selectedFramework.completion_percentage}%
            </div>
            <div className="text-sm text-gray-600">Complete</div>
           {isSuperAdmin ? (
              <button
                onClick={() => setShowControlModal(true)}
                className="mt-2 inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Add Control
              </button>
            ) : ""}
          </div>
        </div>
      </div>

      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex-1 relative">
            <input
              ref={searchInputRef}
              type="text"
              defaultValue={controlsSearchTerm}
              onInput={(e) => {
                // Update clear button visibility without state update
                const clearBtn =
                  e.target.nextElementSibling?.nextElementSibling;
                if (
                  clearBtn &&
                  clearBtn.classList.contains("clear-search-btn")
                ) {
                  if (e.target.value) {
                    clearBtn.style.display = "block";
                  } else {
                    clearBtn.style.display = "none";
                  }
                }
              }}
              placeholder="Search controls by code, title, description, or category... (Click search icon to search)"
              className="w-full px-4 py-2 pl-10 pr-24 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3 pointer-events-none" />

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (searchInputRef.current) {
                  searchInputRef.current.value = "";
                  e.target.style.display = "none";
                }
                setControlsSearchTerm("");
                const frameworkId = getFrameworkId(selectedFramework);
                if (frameworkId) {
                  loadControls(frameworkId, 1, controlsPagination.pageSize, "");
                }
                setTimeout(() => searchInputRef.current?.focus(), 0);
              }}
              className="clear-search-btn absolute right-12 top-2 p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
              style={{ display: controlsSearchTerm ? "block" : "none" }}
              title="Clear search"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={handleControlsSearch}
              className="absolute right-2 top-2 p-1.5 rounded-md transition-colors bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
              title="Search"
            >
              <MagnifyingGlassIcon className="h-4 w-4 text-white" />
            </button>
          </div>

          <div className="text-sm text-gray-600 whitespace-nowrap">
            {controlsLoading ? (
              <span className="text-blue-600">Loading...</span>
            ) : controlsPagination.totalCount > 0 ? (
              <span>
                Showing{" "}
                {(controlsPagination.currentPage - 1) *
                  controlsPagination.pageSize +
                  1}
                -
                {Math.min(
                  controlsPagination.currentPage * controlsPagination.pageSize,
                  controlsPagination.totalCount
                )}{" "}
                of {controlsPagination.totalCount} controls
              </span>
            ) : (
              <span>No controls found</span>
            )}
          </div>
        </div>
      </div> */}
<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative">
<div className="flex items-center justify-between mb-6">
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
      <FunnelIcon className="w-5 h-5 text-blue-600" />
    </div>

    <div>
      <h3 className="text-lg font-semibold text-gray-900">
        Control Filters
      </h3>

      <p className="text-xs text-gray-500">
        {controlsLoading || assignedControlsLoading ? (
          <span className="text-blue-600">Loading…</span>
        ) : (
          <>
            Showing{" "}
            <span className="font-semibold text-blue-600">
              {controls.length}
            </span>{" "}
            control{controls.length !== 1 ? "s" : ""}
            {hasActiveControlFilters && " with active filters"}
          </>
        )}
      </p>
    </div>
  </div>

  
  {/* {isSuperAdmin && (
    <button
      onClick={() => setShowControlModal(true)}
      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
    >
      <PlusIcon className="w-4 h-4 mr-2" />
      Add Control
    </button>
  )} */}
</div>


  {/* Filters Grid */}
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

    {/* 🔍 Search (ADMIN ONLY) */}
    {isSuperAdmin && (
      <div className="space-y-2">
        <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
          <MagnifyingGlassIcon className="w-4 h-4 mr-1.5 text-blue-600" />
          Search
        </label>
        <input
          type="text"
          value={controlsFilters.search}
          onChange={(e) =>
            setControlsFilters(p => ({ ...p, search: e.target.value }))
          }
          placeholder="Search controls..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                     bg-white focus:ring-2 focus:ring-blue-500
                     focus:border-transparent transition-all"
        />
      </div>
    )}

    {/* 🆔 Control ID */}
    <div className="space-y-2">
      <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
        <IdentificationIcon className="w-4 h-4 mr-1.5 text-indigo-600" />
        Control ID
      </label>
      <input
        type="text"
        value={controlsFilters.control_id}
        onChange={(e) =>
          setControlsFilters(p => ({ ...p, control_id: e.target.value }))
        }
        placeholder="AC-01"
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                   bg-white focus:ring-2 focus:ring-blue-500"
      />
    </div>

    {/* 📝 Control */}
    <div className="space-y-2">
      <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
        <DocumentTextIcon className="w-4 h-4 mr-1.5 text-purple-600" />
        Control
      </label>
      <input
        type="text"
        value={controlsFilters.title}
        onChange={(e) =>
          setControlsFilters(p => ({ ...p, title: e.target.value }))
        }
        placeholder="Control title"
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                   bg-white focus:ring-2 focus:ring-blue-500"
      />
    </div>

    {/* 👤 Assigned / 📅 Date (CUSTOMER ADMIN) */}
    {!isSuperAdmin && (
      <>
        <div className="space-y-2">
          <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
            <UserIcon className="w-4 h-4 mr-1.5 text-green-600" />
            Assigned To
          </label>
          <input
            type="text"
            value={controlsFilters.assigned_to}
            onChange={(e) =>
              setControlsFilters(p => ({ ...p, assigned_to: e.target.value }))
            }
            placeholder="Employee name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                       bg-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
            <CalendarDaysIcon className="w-4 h-4 mr-1.5 text-orange-600" />
            Due Date
          </label>
          <input
            type="date"
            value={controlsFilters.date}
            onChange={(e) =>
              setControlsFilters(p => ({ ...p, date: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                       bg-white focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </>
    )}
  {/* 🧹 Clear All — PLATFORM / ADMIN ONLY */}
{isSuperAdmin && (
  <div className="space-y-2">
    <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
      <XCircleIcon className="w-4 h-4 mr-1.5 text-gray-500" />
      Clear Filters
    </label>
    <button
      onClick={() =>
        setControlsFilters({
          search: "",
          control_id: "",
          title: "",
          assigned_to: "",
          date: ""
        })
      }
      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                 bg-gray-50 hover:bg-gray-100
                 focus:ring-2 focus:ring-blue-500
                 transition-all duration-200"
    >
      Clear All
    </button>
  </div>
)}



  </div>

  {/* Active Filters */}
  {Object.values(controlsFilters).some(Boolean) && (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-xs font-medium text-gray-600">
          Active Filters:
        </span>

        {controlsFilters.control_id && (
          <span className="inline-flex items-center px-3 py-1 rounded-full
                           text-xs font-medium bg-indigo-100 text-indigo-800">
            Control ID: {controlsFilters.control_id}
            <button
              onClick={() =>
                setControlsFilters(p => ({ ...p, control_id: "" }))
              }
              className="ml-1.5"
            >
              ✕
            </button>
          </span>
        )}

        {controlsFilters.title && (
          <span className="inline-flex items-center px-3 py-1 rounded-full
                           text-xs font-medium bg-purple-100 text-purple-800">
            Control: {controlsFilters.title}
            <button
              onClick={() =>
                setControlsFilters(p => ({ ...p, title: "" }))
              }
              className="ml-1.5"
            >
              ✕
            </button>
          </span>
        )}

        {!isSuperAdmin && controlsFilters.assigned_to && (
          <span className="inline-flex items-center px-3 py-1 rounded-full
                           text-xs font-medium bg-green-100 text-green-800">
            Assigned: {controlsFilters.assigned_to}
            <button
              onClick={() =>
                setControlsFilters(p => ({ ...p, assigned_to: "" }))
              }
              className="ml-1.5"
            >
              ✕
            </button>
          </span>
        )}

        {!isSuperAdmin && controlsFilters.date && (
          <span className="inline-flex items-center px-3 py-1 rounded-full
                           text-xs font-medium bg-orange-100 text-orange-800">
            Due: {controlsFilters.date}
            <button
              onClick={() =>
                setControlsFilters(p => ({ ...p, date: "" }))
              }
              className="ml-1.5"
            >
              ✕
            </button>
          </span>
        )}

        {/* <button
          onClick={() =>
            setControlsFilters({
              search: "",
              control_id: "",
              title: "",
              assigned_to: "",
              date: ""
            })
          }
          className="ml-2 text-xs text-gray-600 hover:text-gray-800 underline"
        >
          Clear all
        </button> */}
      </div>
    </div>
  )}
  </div>


      {/* Controls Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {controlsLoading && controls.length > 0 && <ApiLoader variant="bar" />}
        {controlsLoading && controls.length === 0 ? (
          <ApiLoader label="Loading controls..." className="py-12" />
        ) : controls.length === 0 && !assignedControlsLoading ? (
  <div className="flex flex-col items-center justify-center py-12 space-y-4">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
      <MagnifyingGlassIcon className="w-8 h-8 text-gray-400" />
    </div>
    <p className="text-gray-600 font-medium">No controls found</p>
    {controlsSearchTerm && (
      <p className="text-sm text-gray-500">
        Try adjusting your search term or{" "}
        <button
          onClick={() => {
            setControlsSearchTerm("");
            if (searchInputRef.current) {
              searchInputRef.current.value = "";
            }
            const frameworkId = getFrameworkId(selectedFramework);
            if (frameworkId) loadControls(frameworkId, 1);
          }}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          clear filters
        </button>
      </p>
    )}
          </div>
        ) : (
          <DataTable
            columns={isSuperAdmin ? adminColumns : nonAdminColumns}
            data={controls}
            getRowId={(control) => String(control.id)}
            loading={controlsLoading || assignedControlsLoading}
            emptyMessage="No controls found"
            manualPagination
            rowCount={controlsPagination.totalCount || 0}
            pagination={{ pageIndex: (controlsPagination.currentPage || 1) - 1, pageSize: controlsPagination.pageSize }}
            onPaginationChange={(updater) => {
              const current = { pageIndex: (controlsPagination.currentPage || 1) - 1, pageSize: controlsPagination.pageSize };
              const next = typeof updater === "function" ? updater(current) : updater;
              handleControlsPageChange(next.pageIndex + 1, next.pageSize);
            }}
            enableColumnVisibility={false}
            enableColumnResizing
            maxBodyHeight="65vh"
          />
        )}
      </div>
    </div>
  );
};

export default ControlsTab;
